import {
  NOTIFICATION_VIRTUAL_CHANNELS,
  type NotificationTemplateItem,
  type PdfDocumentTemplateConfig,
  PdfDocumentTemplateConfigSchema,
  type TestSendNotificationResponse,
  type WhatsAppTemplateProviderConfig,
  WhatsAppTemplateProviderConfigSchema,
} from '@utazzo/shared';
import {
  UzBadge,
  UzButton,
  type UzColumn,
  UzConfirmDialog,
  UzDrawer,
  UzInput,
  UzPagination,
  UzSelect,
  UzSwitch,
  UzTable,
  UzTextarea,
} from '@utazzo/ui';
import { Eye, FileText, Mail, MessageCircle, Pencil, Plus, Send, Trash2, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import {
  useCreateNotificationTemplate,
  useDeleteNotificationTemplate,
  useNotificationChannels,
  useNotificationEvents,
  useNotificationTemplates,
  usePreviewNotificationTemplate,
  useTestSendNotification,
  useUpdateNotificationTemplate,
} from '../../use-notifications';

import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';

export const NotificationTemplatesTab = (): React.JSX.Element => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [eventCode, setEventCode] = useState<string>('');
  const [channelCode, setChannelCode] = useState<string>('');

  const eventsQuery = useNotificationEvents({ page: 1, pageSize: 200, sortDir: 'asc' });
  const channelsQuery = useNotificationChannels();
  const events = useMemo(() => eventsQuery.data?.data ?? [], [eventsQuery.data]);
  const channels = useMemo(() => channelsQuery.data?.data ?? [], [channelsQuery.data]);
  const eventOptions = useMemo(
    () => [
      { value: '', label: 'All events' },
      ...events.map((e) => ({ value: e.code, label: e.code })),
    ],
    [events],
  );
  /**
   * Every channel a template can be addressed to - the delivery transports from
   * `notification_channels`, plus the virtual ones that exist only in code.
   *
   * `pdf` is the virtual one: a PDF document is not delivered, so it has no row
   * in that table (and deliberately no `DOCUMENT` value in the Postgres enum -
   * see NOTIFICATION_CHANNEL_KINDS). It still belongs in this picker, because a
   * booking's e-ticket wording and its confirmation email are two faces of the
   * same event and editing them in two different screens is how they drift.
   */
  const channelChoices = useMemo<Array<{ code: string; kind: string }>>(
    () => [
      ...channels.map((c) => ({ code: c.code, kind: c.kind as string })),
      ...NOTIFICATION_VIRTUAL_CHANNELS.map((c) => ({ code: c.code, kind: c.kind as string })),
    ],
    [channels],
  );
  const channelOptions = useMemo(
    () => [
      { value: '', label: 'All channels' },
      ...channelChoices.map((c) => ({ value: c.code, label: c.code })),
    ],
    [channelChoices],
  );

  const query = {
    page,
    pageSize,
    sortDir: 'asc' as const,
    sortBy: 'eventCode',
    ...(eventCode ? { eventCode } : {}),
    ...(channelCode ? { channelCode } : {}),
  };
  const templatesQuery = useNotificationTemplates(query);
  const updateTpl = useUpdateNotificationTemplate();
  const deleteTpl = useDeleteNotificationTemplate();

  const [editing, setEditing] = useState<NotificationTemplateItem | null>(null);
  const [viewing, setViewing] = useState<NotificationTemplateItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<NotificationTemplateItem | null>(null);

  const data = templatesQuery.data?.data ?? [];

  const columns: ReadonlyArray<UzColumn<NotificationTemplateItem>> = [
    {
      id: 'event',
      header: 'Event',
      cell: (row) => <span className="font-mono text-xs text-zinc-800">{row.eventCode}</span>,
      width: '220px',
    },
    {
      id: 'channel',
      header: 'Channel',
      // A PDF row is not a message. It sits in the same list as the emails for
      // the same event - which is the point - so it is marked rather than
      // separated, or an operator scanning the list reads a document template
      // as something that gets sent to somebody.
      cell: (row) => (
        <UzBadge tone={row.channelCode === 'pdf' ? 'outline' : 'secondary'} pill>
          {row.channelCode === 'pdf' && <FileText className="mr-1 inline h-3 w-3" aria-hidden />}
          {row.channelCode}
        </UzBadge>
      ),
      width: '120px',
    },
    {
      id: 'locale',
      header: 'Locale',
      cell: (row) => <span className="text-xs text-zinc-700">{row.locale}</span>,
      width: '80px',
    },
    {
      id: 'name',
      header: 'Name',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-zinc-900">{row.name}</span>
          {row.subject && <span className="truncate text-xs text-zinc-500">{row.subject}</span>}
        </div>
      ),
    },
    {
      id: 'version',
      header: 'v',
      cell: (row) => <span className="font-mono text-xs text-zinc-700">{row.version}</span>,
      width: '50px',
      align: 'right',
    },
    {
      id: 'active',
      header: 'Active',
      cell: (row) => (
        <UzSwitch
          checked={row.isActive}
          disabled={updateTpl.isPending}
          onCheckedChange={(checked) =>
            updateTpl.mutate({ id: row.id, payload: { isActive: checked } })
          }
        />
      ),
      width: '80px',
    },
    {
      id: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <UzButton variant="ghost" size="sm" onClick={() => setViewing(row)} title="View">
            <Eye className="h-4 w-4" />
          </UzButton>
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => setEditing(row)}
            title="Edit / preview"
          >
            <Pencil className="h-4 w-4" />
          </UzButton>
          <UzButton variant="ghost" size="sm" onClick={() => setDeleting(row)} title="Delete">
            <Trash2 className="h-4 w-4" />
          </UzButton>
        </div>
      ),
      width: '140px',
      align: 'right',
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <UzSelect
          label="Event"
          value={eventCode}
          onValueChange={(v: string) => {
            setPage(1);
            setEventCode(v);
          }}
          options={eventOptions}
          containerClassName="w-64"
        />
        <UzSelect
          label="Channel"
          value={channelCode}
          onValueChange={(v: string) => {
            setPage(1);
            setChannelCode(v);
          }}
          options={channelOptions}
          containerClassName="w-44"
        />
        <div className="ml-auto">
          <UzButton size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            New template
          </UzButton>
        </div>
      </div>
      <UzTable
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        loading={templatesQuery.isLoading}
      />
      <UzPagination
        page={page}
        pageSize={pageSize}
        totalItems={templatesQuery.data?.pagination.totalItems ?? 0}
        onPageChange={setPage}
        pageSizeOptions={[20, 50, 100]}
        onPageSizeChange={(s) => {
          setPage(1);
          setPageSize(s);
        }}
      />

      {creating && (
        <TemplateFormDrawer
          mode="create"
          events={events}
          channels={channelChoices}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <TemplateFormDrawer
          mode="edit"
          initial={editing}
          events={events}
          channels={channelChoices}
          onClose={() => setEditing(null)}
        />
      )}
      {viewing && (
        <TemplateFormDrawer
          mode="view"
          initial={viewing}
          events={events}
          channels={channelChoices}
          onClose={() => setViewing(null)}
        />
      )}

      <UzConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete template?"
        description={`"${deleting?.name ?? ''}" will be removed. Any rule referencing it will fall back to the channel default (or fail).`}
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={() => {
          if (deleting) deleteTpl.mutate(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
};

// ============================================================================
// Edit / create drawer with live side-by-side preview
// ============================================================================

interface TemplateFormDrawerProps {
  /// `view` reuses the same surface as edit/create but with every
  /// control disabled and the Save button hidden. Server preview still
  /// works because it's a read-only POST that takes the existing
  /// template id, so the operator can browse the rendered output.
  mode: 'create' | 'edit' | 'view';
  initial?: NotificationTemplateItem;
  events: ReadonlyArray<{ code: string; samplePayload: unknown }>;
  channels: ReadonlyArray<{ code: string; kind: string }>;
  onClose: () => void;
}

const TemplateFormDrawer = ({
  mode,
  initial,
  events,
  channels,
  onClose,
}: TemplateFormDrawerProps): React.JSX.Element => {
  const readOnly = mode === 'view';
  const create = useCreateNotificationTemplate();
  const update = useUpdateNotificationTemplate();
  const preview = usePreviewNotificationTemplate();
  // When viewing, the save mutations are never fired - keep the
  // `pending` flag honest so the input disabled state is driven by
  // `readOnly || pending` everywhere uniformly.
  const pending = create.isPending || update.isPending;
  const inputsDisabled = readOnly || pending;

  const [eventCode, setEventCode] = useState(initial?.eventCode ?? '');
  const [channelCode, setChannelCode] = useState(initial?.channelCode ?? '');
  const [locale, setLocale] = useState(initial?.locale ?? 'en-IN');
  const [name, setName] = useState(initial?.name ?? '');
  const [subject, setSubject] = useState(initial?.subject ?? '');
  const [bodyHtml, setBodyHtml] = useState(initial?.bodyHtml ?? '');
  const [bodyText, setBodyText] = useState(initial?.bodyText ?? '');
  const [variablesJson, setVariablesJson] = useState<string>('{}');
  const [previewMode, setPreviewMode] = useState<'html' | 'text'>('html');

  // ---- WhatsApp-only fields ----
  //
  // Kept as separate pieces of state rather than one JSON textarea:
  // the whole point of this screen is that an operator who has never
  // seen our provider-config shape can map "slot 2 = destination"
  // without learning it.
  const initialWa = useMemo(
    () => parseWhatsAppConfig(initial?.providerConfig),
    [initial?.providerConfig],
  );
  const [campaignName, setCampaignName] = useState(initialWa?.campaignName ?? '');
  const [waParams, setWaParams] = useState<string[]>(initialWa?.params ?? ['']);
  const [waMediaUrl, setWaMediaUrl] = useState(initialWa?.media?.url ?? '');
  const [waMediaFilename, setWaMediaFilename] = useState(initialWa?.media?.filename ?? '');

  // ---- PDF-document-only fields ----
  //
  // Held as a draft object rather than a JSON textarea for the same reason the
  // WhatsApp slots are separate inputs: the operator editing a voucher's small
  // print is a travel person, not someone who should have to keep a JSON
  // structure balanced to change a check-in cut-off.
  const [pdf, setPdf] = useState<PdfDraft>(() => toPdfDraft(initial?.providerConfig));

  // When the event changes (or the drawer first opens), pre-fill the
  // variables JSON with the event's samplePayload so the operator gets
  // an immediate, meaningful preview without hand-crafting variables.
  useEffect(() => {
    const evt = events.find((e) => e.code === eventCode);
    if (!evt) return;
    if (evt.samplePayload && typeof evt.samplePayload === 'object') {
      setVariablesJson(JSON.stringify(evt.samplePayload, null, 2));
    } else {
      setVariablesJson('{}');
    }
  }, [eventCode, events]);

  // Channel-aware default preview mode: email defaults to HTML, in-app
  // and the SMS-family channels default to text.
  useEffect(() => {
    const ch = channels.find((c) => c.code === channelCode);
    if (!ch) return;
    setPreviewMode(ch.kind === 'EMAIL' ? 'html' : 'text');
  }, [channelCode, channels]);

  // Parse the variables JSON for the client-side renderer. Invalid JSON
  // falls back to {} so the preview keeps rendering (with literal
  // `{{name}}` placeholders) rather than going blank.
  const channelKind = channels.find((c) => c.code === channelCode)?.kind ?? 'IN_APP';
  const isWhatsApp = channelKind === 'WHATSAPP';
  const isPdf = channelKind === 'DOCUMENT';
  /// Both channels replace the subject/HTML editor with their own fields.
  const hasCustomEditor = isWhatsApp || isPdf;

  const parsedVariables = useMemo<Record<string, unknown>>(() => {
    try {
      const v = JSON.parse(variablesJson || '{}');
      return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }, [variablesJson]);

  // Live (client-side) preview - mirrors the server's interpolate()
  // logic exactly so the operator sees what the server WILL render,
  // without a round trip per keystroke. Server-side `Render` button
  // is still available as the canonical verification.
  const rendered = useMemo(
    () => ({
      subject: subject ? interpolate(subject, parsedVariables) : null,
      bodyHtml: bodyHtml ? interpolate(bodyHtml, parsedVariables) : null,
      bodyText: bodyText ? interpolate(bodyText, parsedVariables) : null,
      // Params resolved against the sample payload. The server renders
      // a MISSING variable to an empty string here (not the literal
      // placeholder, which is the rule for bodies) because an
      // unresolved token would reach the customer's phone looking
      // broken - so the preview mirrors that, and an accidentally-blank
      // slot is visible before anyone hits Save.
      whatsappParams: waParams.map((p) => interpolate(p, parsedVariables, '')),
    }),
    [subject, bodyHtml, bodyText, waParams, parsedVariables],
  );

  /**
   * The document as the traveller would read it.
   *
   * Interpolated with `''` for a missing variable, not the literal
   * `{{placeholder}}` the bodies use. That mirrors the server's rule for
   * provider configs and it is the right rule here: this text is printed on a
   * voucher shown at a reception desk, where a stray `{{pnr}}` reads as a
   * forged document rather than as a QA hint.
   */
  const pdfPreview = useMemo(() => {
    if (!isPdf) return null;
    const draft = fromPdfDraft(pdf);
    return {
      kicker: interpolate(draft.kicker, parsedVariables, ''),
      documentTitle: interpolate(draft.documentTitle, parsedVariables, ''),
      fileName: `${draft.fileNamePrefix}-${interpolate('{{reference}}', parsedVariables, 'reference')}.pdf`,
      statusLabel: draft.statusLabel ? interpolate(draft.statusLabel, parsedVariables, '') : '',
      statusDetail: draft.statusDetail ? interpolate(draft.statusDetail, parsedVariables, '') : '',
      watermark: draft.watermark ?? '',
      headings: Object.entries(draft.headings).map(([key, value]) => ({
        key,
        value: interpolate(value, parsedVariables, ''),
      })),
      notes: Object.entries(draft.notes).map(([key, lines]) => ({
        key,
        lines: lines.map((line) => interpolate(line, parsedVariables, '')),
      })),
      footNotes: draft.footNotes.map((line) => interpolate(line, parsedVariables, '')),
    };
  }, [isPdf, pdf, parsedVariables]);

  // Server-side preview button: forces the saved row to re-render so
  // the operator can confirm a save took effect.
  const [serverRendered, setServerRendered] = useState<typeof rendered | null>(null);
  const runServerPreview = (): void => {
    if (!initial) return;
    preview.mutate(
      { id: initial.id, payload: { variables: parsedVariables } },
      {
        onSuccess: (r) =>
          setServerRendered({
            subject: r.subject,
            bodyHtml: r.bodyHtml,
            bodyText: r.bodyText,
            // The server resolves provider params with the same rules
            // the dispatcher uses, so this is the authoritative answer
            // to "what would actually reach DoubleTick".
            whatsappParams: parseWhatsAppConfig(r.providerConfig)?.params ?? [],
          }),
      },
    );
  };

  const eventOpts = events.map((e) => ({ value: e.code, label: e.code }));
  const channelOpts = channels.map((c) => ({ value: c.code, label: c.code }));

  // The provider config we'd persist. Null for non-provider channels;
  // null (rather than a half-built object) for WhatsApp until it is
  // actually valid, which is what gates the Save button - the server
  // rejects an invalid one anyway, and finding that out from a red
  // toast after typing a whole form is a worse way to learn it.
  const pdfConfig = useMemo<PdfDocumentTemplateConfig | null>(() => {
    if (!isPdf) return null;
    const parsed = PdfDocumentTemplateConfigSchema.safeParse(fromPdfDraft(pdf));
    return parsed.success ? parsed.data : null;
  }, [isPdf, pdf]);

  const providerConfig = useMemo<WhatsAppTemplateProviderConfig | null>(() => {
    if (!isWhatsApp) return null;
    const candidate = {
      provider: 'doubletick' as const,
      campaignName: campaignName.trim(),
      params: waParams.map((p) => p.trim()).filter((p) => p.length > 0),
      ...(waMediaUrl.trim()
        ? {
            media: {
              url: waMediaUrl.trim(),
              ...(waMediaFilename.trim() ? { filename: waMediaFilename.trim() } : {}),
            },
          }
        : {}),
    };
    const parsed = WhatsAppTemplateProviderConfigSchema.safeParse(candidate);
    return parsed.success ? parsed.data : null;
  }, [isWhatsApp, campaignName, waParams, waMediaUrl, waMediaFilename]);

  const submit = (): void => {
    if (mode === 'create') {
      create.mutate(
        {
          eventCode: eventCode.trim(),
          channelCode: channelCode.trim(),
          locale: locale.trim() || 'en-IN',
          name: name.trim(),
          subject: isPdf ? undefined : subject.trim() || undefined,
          bodyHtml: isPdf ? undefined : bodyHtml || undefined,
          bodyText: bodyText || undefined,
          providerConfig: pdfConfig ?? providerConfig,
        },
        { onSuccess: onClose },
      );
    } else if (initial) {
      update.mutate(
        {
          id: initial.id,
          payload: {
            name: name.trim(),
            // A document has no subject and no HTML body, and sending the
            // editor's empty strings would write them over whatever the row
            // held. Left undefined so the server keeps the stored values.
            ...(isPdf ? {} : { subject: subject.trim() || undefined, bodyHtml }),
            bodyText,
            providerConfig: pdfConfig ?? providerConfig,
          },
        },
        { onSuccess: onClose },
      );
    }
  };

  // WhatsApp can't be saved without a campaign - there'd be nothing to
  // send. Everything else only needs the identity fields.
  const canSave =
    !pending &&
    Boolean(eventCode) &&
    Boolean(channelCode) &&
    Boolean(name.trim()) &&
    (!isWhatsApp || providerConfig !== null) &&
    // A document with no title or no file name would download as a blank
    // voucher named after nothing. Gated here as well as on the server so the
    // operator learns it from the disabled button, not a red toast.
    (!isPdf || pdfConfig !== null);

  return (
    <UzDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={
        mode === 'create'
          ? 'New template'
          : mode === 'view'
            ? `View ${initial?.name ?? ''}`
            : `Edit ${initial?.name ?? ''}`
      }
      description={
        readOnly
          ? 'Read-only preview. Use the Render saved button to see how this template renders against the sample payload.'
          : `Live preview updates as you type. Use {{variableName}} placeholders. Channel: ${channelCode || '(pick one)'}`
      }
      // Override default 480px to fit a 2-column layout. The form sits
      // on the left at ~440px; the preview pane takes the remainder.
      contentClassName="sm:w-[min(1080px,95vw)]! sm:max-w-none!"
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={onClose} disabled={pending}>
            {readOnly ? 'Close' : 'Cancel'}
          </UzButton>
          {(mode === 'edit' || mode === 'view') && (
            <UzButton variant="outline" onClick={runServerPreview} disabled={preview.isPending}>
              <Eye className="mr-1.5 h-4 w-4" />
              Render saved
            </UzButton>
          )}
          {!readOnly && (
            <UzButton onClick={submit} disabled={!canSave}>
              {mode === 'create' ? 'Create' : 'Save'}
            </UzButton>
          )}
        </div>
      }
    >
      <div className="grid h-full min-h-0 grid-cols-1 gap-4 lg:grid-cols-[440px_1fr]">
        {/* ---------- Left column: editor ---------- */}
        <div className="flex flex-col gap-3 overflow-y-auto pr-1">
          <UzSelect
            label="Event"
            value={eventCode}
            onValueChange={(v: string) => setEventCode(v)}
            options={eventOpts}
            disabled={inputsDisabled || mode === 'edit'}
          />
          <UzSelect
            label="Channel"
            value={channelCode}
            onValueChange={(v: string) => setChannelCode(v)}
            options={channelOpts}
            disabled={inputsDisabled || mode === 'edit'}
          />
          <UzInput
            label="Locale"
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            disabled={inputsDisabled || mode === 'edit'}
          />
          <UzInput
            label="Display name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={inputsDisabled}
            required
          />
          {isPdf ? (
            <PdfDocumentFields draft={pdf} setDraft={setPdf} disabled={inputsDisabled} />
          ) : isWhatsApp ? (
            <WhatsAppCampaignFields
              campaignName={campaignName}
              setCampaignName={setCampaignName}
              params={waParams}
              setParams={setWaParams}
              mediaUrl={waMediaUrl}
              setMediaUrl={setWaMediaUrl}
              mediaFilename={waMediaFilename}
              setMediaFilename={setWaMediaFilename}
              disabled={inputsDisabled}
            />
          ) : (
            <>
              <UzInput
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={inputsDisabled}
                hint="Email subject line. For in-app, used as the headline."
              />
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-zinc-700">Body (HTML)</label>
                <RichTextEditor
                  value={bodyHtml}
                  onChange={setBodyHtml}
                  disabled={inputsDisabled}
                  placeholder="Type your message. Use {{variableName}} for placeholders."
                  stickyTop="flush"
                />
                <p className="text-[11px] text-zinc-500">
                  {channelKind === 'EMAIL'
                    ? 'Primary body for email. Layout is fixed - operators edit content, not theme.'
                    : 'Used by clients that render HTML. Variable tokens ({{name}}) pass through unchanged.'}
                </p>
              </div>
            </>
          )}
          <UzTextarea
            label={
              isPdf ? 'Description' : isWhatsApp ? 'Body (text) - audit copy only' : 'Body (text)'
            }
            rows={5}
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            disabled={inputsDisabled}
            hint={
              channelKind === 'EMAIL'
                ? 'Plain-text fallback. Most modern clients render HTML but this keeps the message readable everywhere.'
                : channelKind === 'IN_APP'
                  ? 'Shown in the bell dropdown.'
                  : isWhatsApp
                    ? 'NOT delivered. The customer receives the Meta-approved template via DoubleTick. This is stored on the notification row so the History tab shows roughly what was sent - keep it in step with the approved wording.'
                    : isPdf
                      ? 'Never printed on the document. It is what this row shows in the list above, so write it for the next operator: what the document is and when a traveller gets it.'
                      : 'Primary body for this channel.'
            }
          />
          <UzTextarea
            label="Variables (JSON)"
            rows={6}
            value={variablesJson}
            onChange={(e) => setVariablesJson(e.target.value)}
            disabled={inputsDisabled}
            hint="Pre-filled from the event's sample payload. Edit to test alternate scenarios."
          />
        </div>

        {/* ---------- Right column: live preview ---------- */}
        <div className="flex min-h-0 flex-col gap-3 overflow-y-auto rounded-md border border-zinc-200 bg-zinc-50 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
              <Eye className="h-4 w-4" /> Live preview
              <span className="font-mono text-[10px] text-zinc-500">
                ({channelKind.toLowerCase()})
              </span>
            </div>
            {/* WhatsApp has no HTML form - the delivered message is a
                Meta-approved template, so the toggle would be a choice
                between "text" and nothing. */}
            {!hasCustomEditor && (
              <UzSelect
                value={previewMode}
                onValueChange={(v: string) => setPreviewMode(v as 'html' | 'text')}
                options={[
                  { value: 'html', label: 'HTML' },
                  { value: 'text', label: 'Text' },
                ]}
                containerClassName="w-32"
              />
            )}
          </div>

          <PreviewEnvelope
            channelKind={channelKind}
            previewMode={previewMode}
            rendered={rendered}
            campaignName={campaignName}
            pdf={pdfPreview}
          />

          {serverRendered && (
            <div className="rounded-md border border-indigo-200 bg-indigo-50 p-2">
              <div className="mb-1 text-[11px] font-medium text-indigo-700">
                Server render (last saved row)
              </div>
              <PreviewEnvelope
                channelKind={channelKind}
                previewMode={previewMode}
                rendered={serverRendered}
                campaignName={campaignName}
                pdf={pdfPreview}
              />
            </div>
          )}

          {/* A document is never sent, so there is nothing to test-send. The
              panel is hidden rather than disabled: an operator who reads
              "Send a real test" on a voucher will reasonably expect one. */}
          {initial && !isPdf && (
            <TestSendPanel
              templateId={initial.id}
              channelKind={channelKind}
              variables={parsedVariables}
            />
          )}
        </div>
      </div>
    </UzDrawer>
  );
};

// ============================================================================
// Test send
// ============================================================================

/**
 * Fire one real message through the channel, from the SAVED template.
 *
 * This is the only check that proves an end-to-end WhatsApp setup:
 * "Test connection" on the channel drawer validates the API key but
 * the preview pane can't know whether the template exists or is
 * approved. A send either arrives on a phone or comes back with the
 * provider's own error.
 *
 * Two things it deliberately makes obvious:
 *   - It uses the last SAVED template, not the current editor state.
 *     Sending the draft would test something that isn't what fires in
 *     production.
 *   - A 200 response is not a success. Permanent channel failures are
 *     recorded terminally rather than thrown, so the outcome is read
 *     off the returned status, not off "the request didn't error".
 */
const TestSendPanel = ({
  templateId,
  channelKind,
  variables,
}: {
  templateId: string;
  channelKind: string;
  variables: Record<string, unknown>;
}): React.JSX.Element => {
  const testSend = useTestSendNotification();
  const [address, setAddress] = useState('');
  const [result, setResult] = useState<TestSendNotificationResponse | null>(null);

  const isWhatsApp = channelKind === 'WHATSAPP';
  const isInApp = channelKind === 'IN_APP';

  const run = (): void => {
    setResult(null);
    testSend.mutate(
      {
        templateId,
        variables,
        ...(isInApp ? {} : { recipientAddress: address.trim() }),
      },
      { onSuccess: (r) => setResult(r) },
    );
  };

  const tone =
    result?.status === 'SENT'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : result?.status === 'SKIPPED'
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-red-200 bg-red-50 text-red-800';

  return (
    <div className="rounded-md border border-zinc-200 bg-white p-3">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-zinc-900">
        <Send className="h-3.5 w-3.5" />
        Send a real test
      </div>
      <p className="mb-2 text-[11px] leading-relaxed text-zinc-500">
        Uses the <strong>last saved</strong> template and the variables above. Save your edits
        first, or you&apos;ll be testing the previous version.
        {isWhatsApp && ' This is the only way to confirm the campaign exists and is Live.'}
      </p>
      <div className="flex items-end gap-2">
        {!isInApp && (
          <UzInput
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={isWhatsApp ? '919811916164' : 'you@utazzoholidays.com'}
            disabled={testSend.isPending}
            containerClassName="flex-1"
            aria-label={isWhatsApp ? 'Test WhatsApp number' : 'Test address'}
          />
        )}
        <UzButton
          variant="outline"
          size="sm"
          onClick={run}
          disabled={testSend.isPending || (!isInApp && address.trim().length === 0)}
        >
          {testSend.isPending ? 'Sending...' : 'Send test'}
        </UzButton>
      </div>
      {isWhatsApp && (
        <p className="mt-1 text-[11px] text-zinc-500">
          Digits with country code, no &quot;+&quot;. Any WhatsApp number works for approved
          templates; free-form replies still need the 24h session window.
        </p>
      )}

      {testSend.isError && (
        <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-[11px] text-red-800">
          Request failed: {(testSend.error as Error).message}
        </div>
      )}

      {result && (
        <div className={`mt-2 rounded border p-2 text-[11px] ${tone}`}>
          <div className="font-medium">Result: {result.status}</div>
          {result.errorMessage && (
            <pre className="mt-1 whitespace-pre-wrap">{result.errorMessage}</pre>
          )}
          {result.skipReason && <div className="mt-1">{result.skipReason}</div>}
          {result.providerMessageId && (
            <div className="mt-1 font-mono">id: {result.providerMessageId}</div>
          )}
          <div className="mt-1 opacity-80">
            Logged in History with this status - filter by template to find it.
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// WhatsApp campaign editor
// ============================================================================

/**
 * The WhatsApp half of the template form.
 *
 * There is no message body here on purpose. Meta only delivers
 * business-initiated WhatsApp messages that use a template they
 * approved, and DoubleTick addresses those templates by TEMPLATE
 * NAME with a POSITIONAL parameter array. So what an operator authors
 * is a reference plus a mapping - "template X, and its {{1}} is our
 * {{customerFirstName}}" - and the wording itself is edited in the
 * DoubleTick dashboard, not here.
 *
 * Param count and order are load-bearing: Meta rejects the send
 * outright when the array length doesn't match the approved template.
 * Hence the explicit numbered rows rather than a comma-separated
 * string - the operator can see the slot numbers they are filling.
 */
interface WhatsAppCampaignFieldsProps {
  campaignName: string;
  setCampaignName: (v: string) => void;
  params: string[];
  setParams: (v: string[]) => void;
  mediaUrl: string;
  setMediaUrl: (v: string) => void;
  mediaFilename: string;
  setMediaFilename: (v: string) => void;
  disabled: boolean;
}

const WhatsAppCampaignFields = ({
  campaignName,
  setCampaignName,
  params,
  setParams,
  mediaUrl,
  setMediaUrl,
  mediaFilename,
  setMediaFilename,
  disabled,
}: WhatsAppCampaignFieldsProps): React.JSX.Element => {
  const setParamAt = (index: number, value: string): void =>
    setParams(params.map((p, i) => (i === index ? value : p)));
  const removeParamAt = (index: number): void => setParams(params.filter((_, i) => i !== index));

  return (
    <>
      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-900">
          <MessageCircle className="h-3.5 w-3.5" />
          WhatsApp templates are authored in DoubleTick
        </div>
        <p className="mt-1 text-[11px] leading-relaxed text-emerald-800">
          Meta must approve the wording, so it can&apos;t be edited here. Create the template in
          DoubleTick, wait until it is <strong>Approved</strong>, then point this row at it and map
          each <code className="font-mono">{'{{n}}'}</code> slot to one of the event&apos;s
          variables.
        </p>
      </div>

      <UzInput
        label="DoubleTick template name"
        value={campaignName}
        onChange={(e) => setCampaignName(e.target.value)}
        disabled={disabled}
        required
        hint="Exactly as it appears in DoubleTick's Templates list. A mismatch fails the send (visible in History)."
      />

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-zinc-700">Template parameters</label>
        <p className="text-[11px] text-zinc-500">
          Order matters. Slot {'{{1}}'} is the first row. The count must match the approved template
          exactly or Meta rejects the message.
        </p>
        <div className="flex flex-col gap-1.5">
          {params.map((param, index) => (
            <div key={index} className="flex items-center gap-1.5">
              <span className="w-9 shrink-0 text-center font-mono text-[11px] text-zinc-500">
                {`{{${index + 1}}}`}
              </span>
              <UzInput
                value={param}
                onChange={(e) => setParamAt(index, e.target.value)}
                disabled={disabled}
                placeholder="{{customerFirstName}}"
                containerClassName="flex-1"
              />
              <UzButton
                variant="ghost"
                size="sm"
                onClick={() => removeParamAt(index)}
                disabled={disabled || params.length <= 1}
                aria-label={`Remove parameter ${index + 1}`}
              >
                <X className="h-3.5 w-3.5" />
              </UzButton>
            </div>
          ))}
        </div>
        <UzButton
          variant="outline"
          size="sm"
          onClick={() => setParams([...params, ''])}
          disabled={disabled || params.length >= 30}
          className="self-start"
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add parameter
        </UzButton>
      </div>

      <UzInput
        label="Header media URL (optional)"
        value={mediaUrl}
        onChange={(e) => setMediaUrl(e.target.value)}
        disabled={disabled}
        placeholder="{{coverImageUrl}}"
        hint="Only for IMAGE / DOCUMENT / VIDEO templates. Must be publicly reachable - WhatsApp fetches it server-side and rejects anything it can't GET."
      />
      {mediaUrl.trim() !== '' && (
        <UzInput
          label="Media filename (optional)"
          value={mediaFilename}
          onChange={(e) => setMediaFilename(e.target.value)}
          disabled={disabled}
          placeholder="itinerary.pdf"
          hint="Shown to the customer for document templates."
        />
      )}
    </>
  );
};

// ============================================================================
// PDF document editor
// ============================================================================

/**
 * The document half of the template form.
 *
 * There is no body editor here, and that is the whole design. The e-ticket and
 * the voucher are DRAWN - vector text on an A4 grid, so a confirmation number
 * stays sharp when a receptionist zooms in - and the facts on them come off the
 * booking. What an operator can usefully own is the wording around those facts:
 * what the document calls itself, what each block of rows is headed, and the
 * small print at the foot. Handing them an HTML box instead would promise a
 * control over layout that the renderer does not have.
 *
 * The fields are grouped the way the page reads top to bottom - identity, then
 * the status band, then headings, then notes, then the small print - so an
 * operator looking at a printed voucher can find the field that produced the
 * line in front of them.
 */
interface PdfDraft {
  kicker: string;
  documentTitle: string;
  fileNamePrefix: string;
  statusLabel: string;
  statusDetail: string;
  watermark: string;
  /// Ordered so the rows do not jump around while an operator types into them,
  /// which a plain object keyed by name would do on every re-key.
  headings: Array<{ key: string; value: string }>;
  /// `text` is newline-separated: one paragraph per line. Simpler than a nested
  /// list editor and it matches how the paragraphs read on the page.
  notes: Array<{ key: string; text: string }>;
  footNotes: string;
}

const emptyPdfDraft = (): PdfDraft => ({
  kicker: '',
  documentTitle: '',
  fileNamePrefix: '',
  statusLabel: '',
  statusDetail: '',
  watermark: '',
  headings: [],
  notes: [],
  footNotes: '',
});

/** Stored config -> editable draft. An unparseable blob yields a blank form. */
const toPdfDraft = (value: unknown): PdfDraft => {
  const parsed = parsePdfConfig(value);
  if (!parsed) return emptyPdfDraft();
  return {
    kicker: parsed.kicker,
    documentTitle: parsed.documentTitle,
    fileNamePrefix: parsed.fileNamePrefix,
    statusLabel: parsed.statusLabel ?? '',
    statusDetail: parsed.statusDetail ?? '',
    watermark: parsed.watermark ?? '',
    headings: Object.entries(parsed.headings).map(([key, value2]) => ({ key, value: value2 })),
    notes: Object.entries(parsed.notes).map(([key, lines]) => ({
      key,
      text: lines.join('\n'),
    })),
    footNotes: parsed.footNotes.join('\n'),
  };
};

/**
 * Draft -> the shape that is stored and served.
 *
 * Blank optional fields become `null` rather than `''`. An empty string would
 * make the renderer draw a status band with nothing in it, which is worse than
 * no band: a traveller reads an empty red stripe as something having gone
 * wrong that we could not name.
 */
const fromPdfDraft = (draft: PdfDraft): PdfDocumentTemplateConfig => {
  const headings: Record<string, string> = {};
  for (const row of draft.headings) {
    const key = row.key.trim();
    if (key) headings[key] = row.value;
  }
  const notes: Record<string, string[]> = {};
  for (const row of draft.notes) {
    const key = row.key.trim();
    const lines = splitLines(row.text);
    if (key && lines.length > 0) notes[key] = lines;
  }
  return {
    provider: 'pdf',
    kicker: draft.kicker.trim(),
    documentTitle: draft.documentTitle.trim(),
    fileNamePrefix: draft.fileNamePrefix.trim(),
    statusLabel: draft.statusLabel.trim() || null,
    statusDetail: draft.statusDetail.trim() || null,
    watermark: draft.watermark.trim() || null,
    headings,
    notes,
    footNotes: splitLines(draft.footNotes),
  };
};

/** One paragraph per line, blanks dropped. */
const splitLines = (text: string): string[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

const PdfDocumentFields = ({
  draft,
  setDraft,
  disabled,
}: {
  draft: PdfDraft;
  setDraft: React.Dispatch<React.SetStateAction<PdfDraft>>;
  disabled: boolean;
}): React.JSX.Element => {
  const set = <K extends keyof PdfDraft>(key: K, value: PdfDraft[K]): void =>
    setDraft((d) => ({ ...d, [key]: value }));

  return (
    <>
      <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-sky-900">
          <FileText className="h-3.5 w-3.5" />
          This is a downloadable document, not a message
        </div>
        <p className="mt-1 text-[11px] leading-relaxed text-sky-800">
          Nothing here is sent to anyone. The traveller downloads this from their booking page, and
          the flights, guests, dates and amounts come off the booking. You own the wording around
          them. Turning this row off does not stop the download - it falls back to the wording built
          into the site.
        </p>
      </div>

      <UzInput
        label="Kicker"
        value={draft.kicker}
        onChange={(e) => set('kicker', e.target.value)}
        disabled={disabled}
        required
        placeholder="E-ticket"
        hint="The small line above the title. Two or three words."
      />
      <UzInput
        label="Document title"
        value={draft.documentTitle}
        onChange={(e) => set('documentTitle', e.target.value)}
        disabled={disabled}
        required
        placeholder="Flight ticket {{reference}}"
        hint="Printed at the top and used as the PDF's title. Placeholders work here."
      />
      <UzInput
        label="File name"
        value={draft.fileNamePrefix}
        onChange={(e) => set('fileNamePrefix', e.target.value)}
        disabled={disabled}
        required
        placeholder="utazzo-flight-ticket"
        hint="Lowercase, hyphens, no spaces. The booking reference and .pdf are added for you."
      />

      <div className="border-t border-zinc-200 pt-3">
        <div className="text-xs font-semibold text-zinc-900">Status band</div>
        <p className="mt-0.5 mb-2 text-[11px] leading-relaxed text-zinc-500">
          Leave all three empty for an ordinary, valid document. Fill them in only where the
          document must not be mistaken for a live one - a cancellation record, or a ticket whose
          cancellation is still pending.
        </p>
        <div className="flex flex-col gap-3">
          <UzInput
            label="Banner"
            value={draft.statusLabel}
            onChange={(e) => set('statusLabel', e.target.value)}
            disabled={disabled}
            placeholder="Booking cancelled"
            hint="Short and loud. Printed across the top of the first page."
          />
          <UzInput
            label="Banner detail"
            value={draft.statusDetail}
            onChange={(e) => set('statusDetail', e.target.value)}
            disabled={disabled}
            placeholder="Confirmed on {{resolvedAtLabel}}"
            hint="One line under the banner. Hidden automatically when its placeholder has no value."
          />
          <UzInput
            label="Watermark"
            value={draft.watermark}
            onChange={(e) => set('watermark', e.target.value)}
            disabled={disabled}
            placeholder="Cancelled"
            hint="Repeated diagonally across EVERY page. Only for a document that must never pass as valid - a banner alone is missed on page two."
          />
        </div>
      </div>

      <KeyedRows
        title="Section headings"
        blurb="The heading above each block of rows. Leave a section out and the site uses its own heading, so you only type the ones you want to change."
        rows={draft.headings.map((h) => ({ key: h.key, text: h.value }))}
        setRows={(rows) =>
          set(
            'headings',
            rows.map((r) => ({ key: r.key, value: r.text })),
          )
        }
        addLabel="Add heading"
        placeholderKey="passengers"
        placeholderText="Passengers"
        multiline={false}
        disabled={disabled}
      />

      <KeyedRows
        title="Section notes"
        blurb="Paragraphs printed under a section's rows. One per line. Whether a note APPEARS is decided by the booking - the refund paragraph only prints when there is a refund - so what you edit here is what it says, never whether it shows."
        rows={draft.notes.map((n) => ({ key: n.key, text: n.text }))}
        setRows={(rows) =>
          set(
            'notes',
            rows.map((r) => ({ key: r.key, text: r.text })),
          )
        }
        addLabel="Add note"
        placeholderKey="cancellation"
        placeholderText="This booking has been cancelled with the airline."
        multiline
        disabled={disabled}
      />

      <UzTextarea
        label="Terms / small print"
        rows={6}
        value={draft.footNotes}
        onChange={(e) => set('footNotes', e.target.value)}
        disabled={disabled}
        hint="Printed at the foot of the last page. One paragraph per line - ID requirements, check-in cut-offs, the agency disclaimer."
      />
    </>
  );
};

/**
 * A list of `key -> text` rows.
 *
 * The keys are not a dropdown, deliberately. They are the renderer's section
 * names, and hardcoding a list here would mean the admin needs a deploy every
 * time the document grows a section - and would silently hide any key an
 * operator already has. The seeded templates arrive with every key the renderer
 * knows filled in, so in practice nobody types one; the free field exists so
 * that a new one is possible without waiting for this screen to catch up.
 *
 * Stacks to one column below `sm` so the key and its text are both readable on
 * a phone rather than squeezed into two narrow columns.
 */
const KeyedRows = ({
  title,
  blurb,
  rows,
  setRows,
  addLabel,
  placeholderKey,
  placeholderText,
  multiline,
  disabled,
}: {
  title: string;
  blurb: string;
  rows: Array<{ key: string; text: string }>;
  setRows: (rows: Array<{ key: string; text: string }>) => void;
  addLabel: string;
  placeholderKey: string;
  placeholderText: string;
  multiline: boolean;
  disabled: boolean;
}): React.JSX.Element => {
  const setAt = (index: number, patch: Partial<{ key: string; text: string }>): void =>
    setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  return (
    <div className="border-t border-zinc-200 pt-3">
      <div className="text-xs font-semibold text-zinc-900">{title}</div>
      <p className="mt-0.5 mb-2 text-[11px] leading-relaxed text-zinc-500">{blurb}</p>
      <div className="flex flex-col gap-3">
        {rows.map((row, index) => (
          <div
            key={index}
            className="rounded-md border border-zinc-200 bg-zinc-50/60 p-2 sm:flex sm:items-start sm:gap-2"
          >
            <UzInput
              value={row.key}
              onChange={(e) => setAt(index, { key: e.target.value })}
              disabled={disabled}
              placeholder={placeholderKey}
              aria-label={`${title} section name ${index + 1}`}
              containerClassName="sm:w-40 sm:shrink-0"
            />
            <div className="mt-2 flex flex-1 items-start gap-1.5 sm:mt-0">
              {multiline ? (
                <UzTextarea
                  rows={3}
                  value={row.text}
                  onChange={(e) => setAt(index, { text: e.target.value })}
                  disabled={disabled}
                  placeholder={placeholderText}
                  aria-label={`${title} text ${index + 1}`}
                  containerClassName="flex-1"
                />
              ) : (
                <UzInput
                  value={row.text}
                  onChange={(e) => setAt(index, { text: e.target.value })}
                  disabled={disabled}
                  placeholder={placeholderText}
                  aria-label={`${title} text ${index + 1}`}
                  containerClassName="flex-1"
                />
              )}
              <UzButton
                variant="ghost"
                size="sm"
                onClick={() => setRows(rows.filter((_, i) => i !== index))}
                disabled={disabled}
                aria-label={`Remove ${title.toLowerCase()} ${index + 1}`}
              >
                <X className="h-3.5 w-3.5" />
              </UzButton>
            </div>
          </div>
        ))}
      </div>
      <UzButton
        variant="outline"
        size="sm"
        onClick={() => setRows([...rows, { key: '', text: '' }])}
        disabled={disabled}
        className="mt-2 self-start"
      >
        <Plus className="mr-1 h-3.5 w-3.5" />
        {addLabel}
      </UzButton>
    </div>
  );
};

/** Stored blob -> the PDF shape, or null when it is something else. */
const parsePdfConfig = (value: unknown): PdfDocumentTemplateConfig | null => {
  if (!value) return null;
  const parsed = PdfDocumentTemplateConfigSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

/**
 * Read a stored `providerConfig` blob into the WhatsApp shape, or null
 * when it isn't one. Tolerant by design - the column is JSON and a row
 * written by a future provider must not crash this form.
 */
const parseWhatsAppConfig = (value: unknown): WhatsAppTemplateProviderConfig | null => {
  if (!value) return null;
  const parsed = WhatsAppTemplateProviderConfigSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

// ============================================================================
// Channel-aware preview envelope
// ============================================================================

interface PdfPreview {
  kicker: string;
  documentTitle: string;
  fileName: string;
  statusLabel: string;
  statusDetail: string;
  watermark: string;
  headings: Array<{ key: string; value: string }>;
  notes: Array<{ key: string; lines: string[] }>;
  footNotes: string[];
}

interface PreviewEnvelopeProps {
  channelKind: string;
  previewMode: 'html' | 'text';
  rendered: {
    subject: string | null;
    bodyHtml: string | null;
    bodyText: string | null;
    whatsappParams?: string[];
  };
  campaignName?: string;
  /// Null for every channel but `pdf`.
  pdf?: PdfPreview | null;
}

const PreviewEnvelope = ({
  channelKind,
  previewMode,
  rendered,
  campaignName,
  pdf,
}: PreviewEnvelopeProps): React.JSX.Element => {
  if (channelKind === 'EMAIL')
    return <EmailEnvelope rendered={rendered} previewMode={previewMode} />;
  if (channelKind === 'IN_APP')
    return <InAppEnvelope rendered={rendered} previewMode={previewMode} />;
  if (channelKind === 'WHATSAPP')
    return <WhatsAppEnvelope rendered={rendered} campaignName={campaignName ?? ''} />;
  if (channelKind === 'DOCUMENT') return <PdfEnvelope pdf={pdf ?? null} />;
  return <GenericEnvelope rendered={rendered} previewMode={previewMode} />;
};

/**
 * The document, roughly as it prints.
 *
 * Deliberately NOT a faithful render of the PDF. Reproducing the real
 * A4 layout here would mean a second renderer to keep in step with the one in
 * the website bundle, and the moment the two drift the preview starts lying -
 * which is worse than obviously being a sketch. What it does show faithfully is
 * every piece of text this template contributes, in the order it appears on the
 * page, with placeholders resolved against the sample payload. The facts
 * between them come off a real booking and are drawn as grey rules, so it is
 * clear which lines this screen owns and which it does not.
 */
const PdfEnvelope = ({ pdf }: { pdf: PdfPreview | null }): React.JSX.Element => {
  if (!pdf) {
    return (
      <div className="rounded-md border border-zinc-200 bg-white p-6 text-center text-xs text-zinc-400">
        Pick the pdf channel to preview a document.
      </div>
    );
  }
  const filler = (widths: string[]): React.JSX.Element => (
    <div className="mt-1.5 flex flex-col gap-1" aria-hidden>
      {widths.map((w, i) => (
        <div key={i} className="h-1.5 rounded-full bg-zinc-200" style={{ width: w }} />
      ))}
    </div>
  );

  return (
    <div className="overflow-hidden rounded-md border border-zinc-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-zinc-100 bg-zinc-50 px-3 py-2">
        <FileText className="h-4 w-4 shrink-0 text-zinc-400" />
        <span className="font-mono text-[11px] break-all text-zinc-600">{pdf.fileName}</span>
      </div>

      <div className="p-4">
        {pdf.statusLabel && (
          <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2">
            <div className="text-xs font-bold tracking-wide text-red-700 uppercase">
              {pdf.statusLabel}
            </div>
            {pdf.statusDetail && (
              <div className="mt-0.5 text-[11px] text-red-800">{pdf.statusDetail}</div>
            )}
          </div>
        )}

        <div className="text-[10px] font-semibold tracking-[1.4px] text-teal-700 uppercase">
          {pdf.kicker || <em className="font-sans tracking-normal text-amber-600">no kicker</em>}
        </div>
        <div className="mt-1 text-base leading-snug font-bold break-words text-[#093f51]">
          {pdf.documentTitle || (
            <em className="text-sm font-normal text-amber-600">no title - this cannot be saved</em>
          )}
        </div>
        {filler(['100%', '72%'])}

        {pdf.headings.length === 0 && pdf.notes.length === 0 ? (
          <p className="mt-4 text-[11px] text-zinc-400">
            No section headings yet - the site will use its own for every block.
          </p>
        ) : (
          <div className="mt-4 flex flex-col gap-3">
            {pdf.headings.map((h) => {
              const note = pdf.notes.find((n) => n.key === h.key);
              return (
                <div key={h.key}>
                  <div className="text-[11px] font-bold tracking-wide text-[#093f51] uppercase">
                    {h.value || <em className="text-amber-600">empty heading</em>}
                  </div>
                  {filler(['92%', '64%'])}
                  {note?.lines.map((line, i) => (
                    <p key={i} className="mt-1.5 text-[11px] leading-relaxed text-zinc-600">
                      {line}
                    </p>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Notes whose section has no heading of its own - the conditional ones
            (a refund paragraph, a partial-cancellation line) live under a
            section the renderer already headed. Shown separately so an operator
            can still read what they wrote. */}
        {pdf.notes.filter((n) => !pdf.headings.some((h) => h.key === n.key)).length > 0 && (
          <div className="mt-4 rounded border border-dashed border-zinc-200 p-2">
            <div className="text-[10px] font-medium text-zinc-500">
              Conditional notes - printed only when the booking calls for them
            </div>
            {pdf.notes
              .filter((n) => !pdf.headings.some((h) => h.key === n.key))
              .map((n) => (
                <div key={n.key} className="mt-1.5">
                  <div className="font-mono text-[10px] text-zinc-400">{n.key}</div>
                  {n.lines.map((line, i) => (
                    <p key={i} className="text-[11px] leading-relaxed text-zinc-600">
                      {line}
                    </p>
                  ))}
                </div>
              ))}
          </div>
        )}

        {pdf.footNotes.length > 0 && (
          <div className="mt-4 border-t border-zinc-200 pt-2">
            {pdf.footNotes.map((line, i) => (
              <p key={i} className="text-[10px] leading-relaxed text-zinc-500">
                {line}
              </p>
            ))}
          </div>
        )}

        {pdf.watermark && (
          <div className="mt-3 flex items-center gap-1.5 text-[10px] text-red-600">
            <span className="rounded border border-red-200 px-1.5 py-0.5 font-bold tracking-widest uppercase">
              {pdf.watermark}
            </span>
            repeated diagonally across every page
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * WhatsApp preview.
 *
 * Deliberately does NOT mock up a chat bubble containing our bodyText:
 * that text is audit copy, not what gets delivered, and a realistic
 * fake bubble would teach the operator the opposite. What it shows is
 * the thing they can actually get wrong and can't see anywhere else -
 * the literal parameter array that will reach DoubleTick, resolved
 * against the sample payload. An empty slot here is the bug.
 */
const WhatsAppEnvelope = ({
  rendered,
  campaignName,
}: {
  rendered: PreviewEnvelopeProps['rendered'];
  campaignName: string;
}): React.JSX.Element => {
  const params = rendered.whatsappParams ?? [];
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-md border border-zinc-200 bg-white p-3 shadow-sm">
        <div className="mb-2 flex items-center gap-1.5 text-[10px] font-medium text-zinc-500">
          <MessageCircle className="h-3 w-3" />
          Outgoing to DoubleTick
        </div>
        <dl className="grid grid-cols-[110px_1fr] gap-x-2 gap-y-1.5 text-xs">
          <dt className="text-zinc-500">Template</dt>
          <dd className="font-mono break-all text-zinc-900">
            {campaignName || <em className="font-sans text-amber-600">not set</em>}
          </dd>
          <dt className="text-zinc-500">Parameters</dt>
          <dd className="text-zinc-900">
            {params.length === 0 ? (
              <em className="text-zinc-400">none</em>
            ) : (
              <ol className="flex flex-col gap-1">
                {params.map((value, index) => (
                  <li key={index} className="flex items-start gap-1.5">
                    <span className="mt-px w-8 shrink-0 font-mono text-[10px] text-zinc-400">
                      {`{{${index + 1}}}`}
                    </span>
                    {value ? (
                      <span className="break-all">{value}</span>
                    ) : (
                      <span className="text-amber-600 italic">
                        empty - variable not in this payload
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </dd>
        </dl>
      </div>

      <div className="rounded-md border border-zinc-200 bg-white p-3 shadow-sm">
        <div className="mb-2 text-[10px] font-medium text-zinc-500">
          Audit copy (stored on the notification row, NOT delivered)
        </div>
        {rendered.bodyText ? (
          <pre className="m-0 max-h-64 overflow-auto rounded bg-emerald-50 p-2 font-mono text-[11px] whitespace-pre-wrap text-zinc-800">
            {rendered.bodyText}
          </pre>
        ) : (
          <div className="p-3 text-center text-xs text-zinc-400">
            No text body - History will show this send with no readable content.
          </div>
        )}
      </div>
    </div>
  );
};

const EmailEnvelope = ({
  rendered,
  previewMode,
}: {
  rendered: PreviewEnvelopeProps['rendered'];
  previewMode: 'html' | 'text';
}): React.JSX.Element => (
  <div className="overflow-hidden rounded-md border border-zinc-200 bg-white shadow-sm">
    {/* Email header */}
    <div className="flex items-start gap-2 border-b border-zinc-100 bg-zinc-50 px-3 py-2">
      <Mail className="mt-0.5 h-4 w-4 text-zinc-400" />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-[11px] text-zinc-500">
          From: <span className="font-mono text-zinc-700">noreply@utazzo.local</span>
        </span>
        <span className="truncate text-sm font-semibold text-zinc-900">
          {rendered.subject || <em className="text-zinc-400">(no subject)</em>}
        </span>
      </div>
    </div>
    {/* Body */}
    {previewMode === 'html' && rendered.bodyHtml ? (
      <SandboxedHtml html={rendered.bodyHtml} />
    ) : previewMode === 'text' && rendered.bodyText ? (
      <pre className="m-0 max-h-[520px] overflow-auto p-3 font-mono text-xs whitespace-pre-wrap text-zinc-800">
        {rendered.bodyText}
      </pre>
    ) : (
      <div className="p-6 text-center text-xs text-zinc-400">
        {previewMode === 'html' ? 'No HTML body.' : 'No text body.'}
      </div>
    )}
  </div>
);

const InAppEnvelope = ({
  rendered,
  previewMode,
}: {
  rendered: PreviewEnvelopeProps['rendered'];
  previewMode: 'html' | 'text';
}): React.JSX.Element => (
  <div className="rounded-md border border-zinc-200 bg-white p-3 shadow-sm">
    <div className="mb-2 text-[10px] font-medium text-zinc-500">Bell dropdown row preview</div>
    <div className="flex items-start gap-2 rounded bg-indigo-50/40 px-2 py-2">
      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-xs font-medium text-zinc-900">
            {rendered.subject || <em className="text-zinc-400">(no subject)</em>}
          </span>
          <span className="shrink-0 text-[10px] text-zinc-500">just now</span>
        </div>
        {previewMode === 'text' && rendered.bodyText && (
          <p className="line-clamp-2 text-[11px] text-zinc-600">{rendered.bodyText}</p>
        )}
        {previewMode === 'html' && rendered.bodyHtml && (
          <SandboxedHtml html={rendered.bodyHtml} compact />
        )}
      </div>
    </div>
  </div>
);

const GenericEnvelope = ({
  rendered,
  previewMode,
}: {
  rendered: PreviewEnvelopeProps['rendered'];
  previewMode: 'html' | 'text';
}): React.JSX.Element => (
  <div className="rounded-md border border-zinc-200 bg-white p-3 shadow-sm">
    {rendered.subject && (
      <div className="mb-2 text-sm font-semibold text-zinc-900">{rendered.subject}</div>
    )}
    {previewMode === 'html' && rendered.bodyHtml ? (
      <SandboxedHtml html={rendered.bodyHtml} />
    ) : previewMode === 'text' && rendered.bodyText ? (
      <pre className="m-0 max-h-[520px] overflow-auto p-1 font-mono text-xs whitespace-pre-wrap text-zinc-800">
        {rendered.bodyText}
      </pre>
    ) : (
      <div className="p-4 text-center text-xs text-zinc-400">
        No {previewMode === 'html' ? 'HTML' : 'text'} body.
      </div>
    )}
  </div>
);

/**
 * Sandbox the email/HTML preview in an iframe so any inline CSS or
 * `<script>` in the template can't mutate the admin's DOM or styles.
 * Auto-sizes to its content (re-measured on srcDoc change).
 */
const SandboxedHtml = ({
  html,
  compact,
}: {
  html: string;
  compact?: boolean;
}): React.JSX.Element => {
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState(compact ? 60 : 240);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame) return;
    const handle = () => {
      try {
        const doc = frame.contentDocument;
        if (!doc) return;
        const h = doc.documentElement.scrollHeight;
        setHeight(Math.min(720, Math.max(compact ? 60 : 120, h)));
      } catch {
        // Cross-origin (shouldn't happen for srcDoc) - keep default.
      }
    };
    frame.addEventListener('load', handle);
    return () => frame.removeEventListener('load', handle);
  }, [html, compact]);

  return (
    <iframe
      ref={iframeRef}
      title="Template preview"
      sandbox="allow-same-origin"
      srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"></head><body style="margin:0;padding:${compact ? '6px 8px' : '14px'};font-family:system-ui,-apple-system,sans-serif;font-size:13px;line-height:1.5;color:#27272a;">${html}</body></html>`}
      style={{ width: '100%', height, border: 0, display: 'block' }}
    />
  );
};

// ============================================================================
// Client-side renderer (mirrors apps/api/.../notification-template.service.ts)
// ============================================================================

/**
 * Mustache-style `{{path.to.value}}` substitution. Mirrors the server
 * renderer one-for-one so the client preview shows EXACTLY what the
 * dispatcher will write to the notification row.
 *
 * - Missing variables stay as the literal `{{name}}` placeholder
 *   (matches the server fallback).
 * - String values pass through; numbers/booleans stringify; objects/
 *   arrays JSON-stringify (debug aid).
 */
const interpolate = (
  source: string,
  variables: Record<string, unknown>,
  missing?: string,
): string => {
  return source.replace(/{{\s*([a-zA-Z0-9_.]+)\s*}}/g, (match, path: string) => {
    const value = lookup(variables, path);
    if (value === undefined || value === null) return missing ?? match;
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
      return JSON.stringify(value);
    } catch {
      return match;
    }
  });
};

const lookup = (variables: Record<string, unknown>, path: string): unknown => {
  const parts = path.split('.');
  let cursor: unknown = variables;
  for (const part of parts) {
    if (cursor === null || cursor === undefined || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return cursor;
};
